/**
 * The one Download button for the Study Documents page (Jim's plan,
 * 2026-10-25). One option downloads in one click; several open a small menu of
 * formats. While a download on the same card runs, every download button on
 * the card is disabled, and the running one reads "Preparing…" with a spinner;
 * a slow one also says "This can take a few minutes" with the elapsed time.
 *
 * The menu is rendered in a portal with fixed positioning, because the cards
 * and the View panels clip their overflow. Keyboard: Enter, Space or Down
 * opens it on the first item, Up on the last; Up/Down/Home/End move; Escape
 * or Tab closes it and returns focus to the button.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Download, Loader2 } from 'lucide-react';
import { downloadButtonState } from './downloadReply';
import type { DownloadOption } from './studyDocumentDownloads';
import type { DocumentDownload } from './useDocumentDownload';

export interface DownloadButtonProps {
  download: DocumentDownload;
  options: DownloadOption[];
  /** Resting label; "Download" unless a caller needs otherwise. */
  label?: string;
  /** primary: the card face. secondary: inside a View panel. */
  variant?: 'primary' | 'secondary';
  size?: 'md' | 'sm';
  /** Disabled for a reason other than a running download. */
  disabled?: boolean;
  title?: string;
  /** Classes for the outer wrapper (e.g. flex-1 on the card face). */
  className?: string;
}

const MENU_ITEM_HEIGHT = 36;

export default function DownloadButton({
  download,
  options,
  label = 'Download',
  variant = 'primary',
  size = 'md',
  disabled,
  title,
  className = '',
}: DownloadButtonProps) {
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; minWidth: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  const ids = options.map((o) => o.request.id);
  const runningOption = options.find((o) => o.request.id === download.runningId);
  const state = downloadButtonState({
    label,
    optionIds: ids,
    runningId: download.runningId,
    runningSlow: download.runningSlow,
    elapsedSeconds: download.startedAt ? (now - download.startedAt) / 1000 : 0,
    disabled: disabled || options.length === 0,
  });

  // Tick the elapsed timer only while this button's slow download runs. Keyed
  // on the running request id and its start time, not on the options (which
  // callers rebuild on every render), so the interval is not restarted.
  const tickKey = runningOption && download.runningSlow ? `${runningOption.request.id}:${download.startedAt}` : null;
  useEffect(() => {
    if (!tickKey) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [tickKey]);

  // A download starting anywhere on the card closes this menu.
  useEffect(() => {
    if (download.runningId) setOpen(false);
  }, [download.runningId]);

  const place = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const height = options.length * MENU_ITEM_HEIGHT + 8;
    const below = window.innerHeight - r.bottom;
    const top = below < height + 8 && r.top > height + 8 ? r.top - height - 4 : r.bottom + 4;
    const minWidth = Math.max(r.width, 200);
    const left = Math.min(Math.max(8, r.right - minWidth), window.innerWidth - minWidth - 8);
    setPos({ top, left, minWidth });
  };

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const close = () => setOpen(false);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus follows the highlighted item while the menu is open.
  useEffect(() => {
    if (open && pos) itemRefs.current[focusIndex]?.focus();
  }, [open, pos, focusIndex]);

  // Click outside closes.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const multi = options.length > 1;

  const start = (option: DownloadOption) => {
    setOpen(false);
    triggerRef.current?.focus();
    void download.run(option.request);
  };

  const openMenu = (index: number) => {
    setFocusIndex(index);
    setOpen(true);
  };

  const onTriggerClick = () => {
    if (state.disabled) return;
    if (!multi) {
      start(options[0]);
      return;
    }
    if (open) setOpen(false);
    else openMenu(0);
  };

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!multi || state.disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      openMenu(0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu(options.length - 1);
    }
  };

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const last = options.length - 1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIndex((i) => (i >= last ? 0 : i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIndex((i) => (i <= 0 ? last : i - 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setFocusIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setFocusIndex(last);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const sizing = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm';
  const icon = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const look =
    variant === 'primary'
      ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-700 hover:shadow active:bg-primary-800'
      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50';
  const singleTitle = !multi && options[0] ? options[0].title : undefined;

  return (
    <div className={`flex flex-col ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={onTriggerClick}
        onKeyDown={onTriggerKeyDown}
        disabled={state.disabled}
        aria-busy={state.spinning || undefined}
        aria-haspopup={multi ? 'menu' : undefined}
        aria-expanded={multi ? open : undefined}
        aria-controls={multi && open ? menuId : undefined}
        title={title ?? singleTitle}
        className={`w-full inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed ${sizing} ${look}`}
      >
        {state.spinning ? <Loader2 className={`${icon} animate-spin`} /> : <Download className={icon} />}
        <span>{state.label}</span>
        {multi && !state.spinning && <ChevronDown className={icon} aria-hidden="true" />}
      </button>
      {state.slowLine && (
        <p className="mt-1 text-[11px] text-gray-500 tabular-nums text-center" role="status">
          {state.slowLine}
        </p>
      )}
      {multi && open && pos &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={`${label} as`}
            onKeyDown={onMenuKeyDown}
            style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: pos.minWidth }}
            className="z-50 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          >
            {options.map((o, i) => (
              <button
                key={o.request.id}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={i === focusIndex ? 0 : -1}
                title={o.title}
                onClick={() => start(o)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-100 focus:outline-none"
              >
                <Download className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                <span>{o.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
